'use client';

import { useMemo, useRef } from 'react';
import { Skeleton } from '@/components/atoms/Skeleton';
import ProdutoHome from '@/components/molecules/ProdutoHome';
import * as S from './styles';
import type { ListagemProdutosHomeProps } from '../..';
import Text from '@/components/atoms/Text';
import { registrarEvento } from '@/services/googleAnalytics';
import { theme } from '@/styles/theme';
import { useRowScrollTracker } from '@/hooks/useRowScrollTracker';
import useBreakpoint from '@/hooks/useBreakpoint';

export default function ListagemProdutosHomeDesktop(
  props: ListagemProdutosHomeProps,
) {
  const { $loading, $produtos, style, $funcaoAdicional } = props;
  const elementRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const { widthWindow } = useBreakpoint();

  const itemsPerRow = useMemo(
    () => (widthWindow >= 1126 ? 4 : 3),
    [widthWindow],
  );

  // Set up scroll tracking for product rows
  const { registerElement } = useRowScrollTracker({
    items: $produtos || [],
    itemsPerRow,
    eventName: 'home_visualizou_gift_card',
    getRowData: (rowItems, rowIndex) => ({
      // Data about the entire row
      linha: rowIndex + 1,
      produtos: rowItems.map((produto) => produto.nome),
    }),
    visibilityThreshold: 0.6,
    enabled: !$loading && !!$produtos && $produtos.length > 0,
  });

  const setElementRef = (
    key: string,
    element: HTMLDivElement | null,
    index: number,
  ) => {
    if (element) {
      elementRefs.current.set(key, element);
      // Only register the first item of each row to avoid duplicate observers
      if (index % itemsPerRow === 0) {
        registerElement(index, element);
      }
    }
  };

  return (
    <S.Container style={style}>
      {$loading || !$produtos
        ? Array.from({ length: 6 }).map((item, index) => (
            <Skeleton
              key={`skeleton_${item}_${index}`}
              style={{ width: 185, height: 120, borderRadius: theme.radius[4] }}
            />
          ))
        : $produtos?.map((produto, index) => (
            <div
              key={produto.id}
              ref={(el) => setElementRef(produto.id, el, index)}
              onClick={() => {
                $funcaoAdicional?.();
                registrarEvento('home_clicou_gift_card', {
                  produtoID: produto?.produtoID,
                  produto: produto?.nome,
                });
              }}
            >
              <ProdutoHome {...produto} />
            </div>
          ))}
      {!$loading && $produtos?.length === 0 ? (
        <Text
          $size={2}
          $color="neutral"
          $weight="medium"
          $align="left"
          $highContrast={false}
          $text="Não possuimos produtos nesta categoria."
        />
      ) : null}
    </S.Container>
  );
}
